import { useFakeCaret } from '@/hooks/useFakeCaret';
import clsx from 'clsx';
import { forwardRef, useEffect, useImperativeHandle, useRef, type InputHTMLAttributes } from 'react';

export interface FakeCaretInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    type?: 'text' | 'password' | 'email' | 'search' | 'tel' | 'url';
    caretClassName?: string;
    caretStyle?: React.CSSProperties;
    containerClassName?: string;
    showCaretWhenBlur?: boolean;
    isCaretVisible?: boolean;
    onCaretVisibilityChange?: (visible: boolean) => void;
    fullWidth?: boolean;
}

export interface FakeCaretInputRef {
    input: HTMLInputElement | null;
    showCaret: () => void;
    hideCaret: () => void;
    updateCaretPosition: () => void;
}

export const FakeCaretInput = forwardRef<FakeCaretInputRef, FakeCaretInputProps>(
    (
        {
            className,
            caretClassName,
            caretStyle,
            containerClassName,
            showCaretWhenBlur = false,
            isCaretVisible: controlledCaretVisible,
            onCaretVisibilityChange,
            onChange,
            onKeyUp,
            onKeyDown,
            onClick,
            onFocus,
            onBlur,
            type = 'text',
            fullWidth = false,
            value,
            style,
            ...restProps
        },
        externalRef
    ) => {
        const { inputRef, isCaretVisible, caretPixelPosition, updateCaretPosition, showCaret, hideCaret } = useFakeCaret({
            showCaretWhenBlur,
            isCaretVisible: controlledCaretVisible,
            onCaretVisibilityChange,
        });

        useEffect(() => {
            if (value === undefined) return;

            const input = inputRef.current;
            if (!input) return;

            const syncCaretAndScroll = () => {
                if (typeof input.setSelectionRange === 'function' && document.activeElement !== input) {
                    const stringValue = typeof value === 'string' ? value : String(value ?? '');
                    const nextPosition = stringValue.length;
                    try {
                        input.setSelectionRange(nextPosition, nextPosition);
                    } catch {
                        // Some input types (e.g. number) do not support setSelectionRange
                    }
                }

                if (document.activeElement !== input) {
                    input.scrollLeft = input.scrollWidth;
                }

                updateCaretPosition();
            };

            const frameId = typeof window !== 'undefined' ? window.requestAnimationFrame(syncCaretAndScroll) : undefined;

            return () => {
                if (frameId !== undefined && typeof window !== 'undefined') {
                    window.cancelAnimationFrame(frameId);
                }
            };
        }, [value, updateCaretPosition, inputRef]);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            updateCaretPosition();
            onChange?.(e);
        };

        const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
            updateCaretPosition();
            onKeyUp?.(e);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            updateCaretPosition();
            onKeyDown?.(e);
        };

        const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
            e.preventDefault();
        };

        const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
            updateCaretPosition();
            onClick?.(e);
        };

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            updateCaretPosition();
            onFocus?.(e);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            onBlur?.(e);
        };

        useImperativeHandle(externalRef, () => ({
            input: inputRef.current,
            showCaret,
            hideCaret,
            updateCaretPosition,
        }));

        const containerRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            const input = inputRef.current;
            const container = containerRef.current;

            const preventTouchDefault = (e: TouchEvent) => {
                e.preventDefault();
            };

            input?.addEventListener('touchstart', preventTouchDefault, { passive: false });
            container?.addEventListener('touchstart', preventTouchDefault, { passive: false });

            return () => {
                input?.removeEventListener('touchstart', preventTouchDefault);
                container?.removeEventListener('touchstart', preventTouchDefault);
            };
        }, [inputRef]);

        return (
            <div
                ref={containerRef}
                className={clsx('relative flex h-full w-full items-center justify-center', containerClassName, {
                    'w-full': fullWidth,
                })}
            >
                <input
                    ref={inputRef}
                    type={type}
                    onChange={handleChange}
                    onMouseDown={handleMouseDown}
                    onKeyUp={handleKeyUp}
                    onKeyDown={handleKeyDown}
                    onClick={handleClick}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onContextMenu={(e) => e.preventDefault()}
                    className={clsx('caret-transparent', className, { 'w-full': fullWidth })}
                    style={{
                        WebkitTapHighlightColor: 'transparent',
                        ...style,
                    }}
                    value={value}
                    {...restProps}
                />

                {isCaretVisible && (
                    <div
                        className={clsx(
                            'animate-caret-blink bg-text-black pointer-events-none absolute top-1/2 h-[calc(20*var(--vh-unit))] w-[calc(2*var(--vw-unit))] -translate-y-1/2',
                            caretClassName
                        )}
                        style={{
                            left: `${caretPixelPosition}px`,
                            ...caretStyle,
                        }}
                    />
                )}
            </div>
        );
    }
);

FakeCaretInput.displayName = 'FakeCaretInput';
